import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  flatMap,
  iif,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from "rxjs";
import { MsalService } from "@azure/msal-angular";
import { TokenClaims } from "@azure/msal-common";
import { ApiService } from "../core/services/api.service";
import { ConfigService } from "../core/services/config.service";
import { UserInfo } from "os";

export type UserProfile = {
  id: string;
  active: boolean;
  email?: string;
  name?: string;
  phone?: string;
  mobile?: string;
};

export type UpdateUserContact = {
  phone: string;
  mobile: string;
};

@Injectable({
  providedIn: "root",
})
export class UserSerivice {
  private userProfle: BehaviorSubject<UserProfile | undefined> =
    new BehaviorSubject(undefined);

  constructor(
    private authService: MsalService,
    private configService: ConfigService,
    private apiService: ApiService
  ) {}

  currentProfile() {
    return this.userProfle.asObservable();
  }

  /***
   * Gets profile form remote
   */
  getProfile() {
    const account = this.authService.instance.getActiveAccount();

    if (!account) {
      this.userProfle.next(undefined);
      return this.userProfle.asObservable();
    }

    const profile$ = this.userProfle.asObservable();

    const getUser = (id: string) =>
      this.apiService.findOne(id, this.configService.GetUserUrl).pipe(
        tap((u) => {
          this.userProfle.next({
            id,
            active: u["isActive"],
            email: u["email"],
            name: u["fullName"],
            mobile: u["mobile"],
            phone: u["phone"],
          });
        }),
        switchMap(() => profile$),
        catchError((e) => {
          if (e["status"] === 404) {
            return of(undefined);
          }
          throw e;
        })
      );

    return this.authService
      .acquireTokenSilent({ scopes: ["openid", "profile"] })
      .pipe(map((i) => i.idTokenClaims))
      .pipe(
        switchMap(({ sub }: { sub: string }) =>
          iif(
            () =>
              this.userProfle.value !== undefined &&
              this.userProfle.getValue().id === sub,
            profile$,
            getUser(sub)
          )
        )
      );
  }

  addUser() {
    const saveUser = (
      idTokenClaims: TokenClaims & {
        [key: string]:
          | string
          | number
          | string[]
          | object
          | undefined
          | unknown;
      }
    ) => {
      const { sub: userKey, name: fullName, signInName: email } = idTokenClaims;

      const user = {
        fullName,
        email,
        userKey,
      };

      return this.apiService.save(this.configService.AddUserUrl, user).pipe(
        tap((u) => {
          this.userProfle.next({ id: userKey, active: false });
          return of({ id: userKey, active: false, added: true });
        })
      );
    };

    return this.authService
      .acquireTokenSilent({ scopes: ["openid", "profile"] })
      .pipe(
        map((t) => t.idTokenClaims as TokenClaims)
      )
      .pipe(mergeMap((idTokenClaims) => saveUser(idTokenClaims)));
  }

  updateProfile(contact: UpdateUserContact) {
     return this.authService
      .acquireTokenSilent({ scopes: ["openid", "profile"] })
      .pipe(
        map((i) => i.idTokenClaims),
        switchMap(({ sub }: { sub: string }) => {
          const userContact = { userKey: sub, ...contact };
          return this.apiService.update(
            this.configService.updateUserProfileUrl(sub),
            userContact
          );
        })
      );
  }

  clearProfile() {
    this.userProfle.next(undefined);
  }
}
